import { isProgramOrder } from '../constants'
import { postScreeningAttachOrder } from '@cannect/services/resources/screening/requests'
import { queryClient } from '@cannect/query-client'
import { buildTrailPath, storedScreeningToken, storedTrailSearch } from './paths'

export { isProgramOrder } from '../constants'

// O cart_id (uuid do pedido) só serve ao pagamento; depois de pago a trilha segue sem ele.
const withoutCartId = (search: string) => {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  params.delete('cart_id')
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const buildProgramSchedulingPath = () =>
  buildTrailPath('agenda', storedScreeningToken() || undefined, withoutCartId(storedTrailSearch()))

// Depois de pago, a trilha mostra a confirmação do pagamento e só então segue para a agenda.
export const buildProgramPaidPath = () =>
  buildTrailPath('pago', storedScreeningToken() || undefined, withoutCartId(storedTrailSearch()))

// Falha mantém o cart_id: "Tentar novamente" volta ao pagamento do mesmo pedido.
export const buildProgramFailedPath = () =>
  buildTrailPath('pagamento-erro', storedScreeningToken() || undefined, storedTrailSearch())

export const paidCheckoutPath = (
  order: { id?: number; is_program_order?: boolean } | null | undefined,
  fallback: string
) => {
  if (isProgramOrder(order)) {
    return buildProgramPaidPath()
  }
  return fallback
}

export type TProgramPaymentSummary = {
  orderId?: number
  method: 'pix' | 'credit'
  amount: number
  installments?: number
  // paid = false marca a tentativa recusada (o resumo também alimenta a tela de erro); token liga o resumo à sessão.
  paid?: boolean
  token?: string
}

const PAYMENT_SUMMARY_KEY = '@CANNECT_PROGRAM_PAYMENT_SUMMARY'

// O pedido é limpo do carrinho assim que o pagamento termina; as telas de resultado leem o resumo guardado aqui.
export const saveProgramPaymentSummary = (summary: TProgramPaymentSummary) => {
  try {
    sessionStorage.setItem(
      PAYMENT_SUMMARY_KEY,
      JSON.stringify({ paid: true, ...summary, token: storedScreeningToken() || undefined })
    )
  } catch {
    /* sem sessionStorage a tela de resultado só omite o resumo */
  }
}

export const readProgramPaymentSummary = (): TProgramPaymentSummary | null => {
  try {
    const raw = sessionStorage.getItem(PAYMENT_SUMMARY_KEY)
    return raw ? (JSON.parse(raw) as TProgramPaymentSummary) : null
  } catch {
    return null
  }
}

// Esta sessão da triagem já pagou o programa neste navegador (o pedido é vinculado pela API logo depois).
export const hasPaidProgramPayment = (token?: string) => {
  const summary = readProgramPaymentSummary()
  return Boolean(token) && Boolean(summary) && summary?.paid !== false && summary?.token === token
}

const ATTACH_ATTEMPTS = 8
const ATTACH_DELAY_MS = 1500

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })

// A API só vincula o pedido depois de persistir o pagamento (webhook do gateway), que pode chegar alguns
// segundos depois da aprovação que o portal enxerga. Tenta algumas vezes antes de seguir para a agenda.
const SESSION_ALREADY_MOVED = 'não está aguardando o pagamento'

const attachWithRetry = async (token: string, orderId: number, attemptsLeft: number): Promise<void> => {
  try {
    const next = await postScreeningAttachOrder(token, { order_id: orderId })
    // A trilha decide a tela pelo estado da triagem em cache; sem isto ela ainda enxergaria "aguardando pagamento".
    queryClient.setQueryData(['screening-next', token], next)
  } catch (error) {
    const response = (error as { response?: { status?: number; data?: { message?: string } } })?.response
    // 400 "Sessão não está aguardando o pagamento": outra chamada já vinculou o pedido, repetir só gera 400 em série.
    if (response?.status === 400 && String(response.data?.message || '').includes(SESSION_ALREADY_MOVED)) {
      await queryClient.invalidateQueries({ queryKey: ['screening-next', token] })
      return
    }
    if (attemptsLeft <= 1) return
    await wait(ATTACH_DELAY_MS)
    await attachWithRetry(token, orderId, attemptsLeft - 1)
  }
}

// Pix, cartão e o efeito de pedido pago chamam o vínculo quase juntos; uma única tentativa por pedido basta.
const attachInFlight = new Map<string, Promise<void>>()

export const attachProgramOrderIfNeeded = async (order?: { id?: number; is_program_order?: boolean } | null) => {
  if (!isProgramOrder(order)) return
  const token = storedScreeningToken()
  const orderId = Number(order?.id)
  if (!token || !orderId) return

  const key = `${token}:${orderId}`
  const running = attachInFlight.get(key)
  if (running) {
    await running
    return
  }

  const attempt = attachWithRetry(token, orderId, ATTACH_ATTEMPTS).finally(() => attachInFlight.delete(key))
  attachInFlight.set(key, attempt)
  await attempt
}
